import type { trackEcommerce } from '@svenskaspelab/nova-core/nextjs/analytics';
import { getISOWeek, parseISO } from 'date-fns';

import { type IPayable } from '../../../@types/Payable.js';
import type { ITSProductId, Product } from '../../../@types/Product.js';
import type { PaidPayableItem } from '../../../@types/Service.js';
import { getObjectValueByKey } from '../../../utils/getObjectValueByKey.js';
import { logger } from '../../../utils/index.js';
import { isWager, type Wager } from '../../../wager.js';
import { convertRoleToTypeOfShare, type MarketplaceShare } from './convertRoleToTypeOfShare.js';
import { fetchAdditionalDrawInfo, type LiteDraw } from './fetchAdditionalDrawInfo.js';
import type { DrawId, ProductToTrack } from './types.js';

type Transaction = Parameters<typeof trackEcommerce>[0];
type TransactionItem = Transaction['items'][number];

type DrawInfo = Promise<
  | {
      draws: Map<DrawId, LiteDraw>;
      jackpots: Map<Product, string[]>;
    }
  | undefined
>;

/**
 * @typedef WagerData
 * @property wagerId - A string property
 * @property wager - An instance of the Wager class
 * @property wagerResponse - The payment response from the API call
 */

type AddonProduct = 'Sportkryss';

export type TrackingContext = {
  bag?: string;
  uniqueTrackingId?: string;
};

const addonProducts = {
  Sportkryss: ['Sportkrysset', 'SPORTJOKER'],
} as const satisfies Record<AddonProduct, [string, ITSProductId]>;

const relatedAddonProductName = {
  Stryktipset: 'Fullträff Stryktipset',
  TopptipsetStryk: 'Fullträff Topptipset',
} as const satisfies Partial<Record<Product, string>>;

const ADDITIONAL_PRODUCTS: Product[] = [
  'Stryktipset',
  'Europatipset',
  'Matchen',
  'Bomben',
  'Maltipset',
  'TopptipsetStryk',
  'TopptipsetExtra',
  'Topptipset',
  'Powerplay',
  'Challenge',
  'Fulltraff',
] as const;

const fetchDrawInfo = async (products: ProductToTrack[]): DrawInfo => {
  try {
    const drawInfo = await fetchAdditionalDrawInfo(products);
    return drawInfo;
  } catch {
    const productLog = products.map(({ product, drawNumber }) => `${product}:${drawNumber}`).join(',');
    logger.info('Unable to provide extra transaction tracking info', { products: productLog });
  }

  return undefined;
};

type PaidPayableWagerItem<TPayable extends IPayable> = PaidPayableItem<TPayable> & { payable: Wager };

/**
 * Creates the transaction information to be sent to the data layers (GTM, EDDL, Luna etc).
 *
 * @param payableData An object with wagerId, payable and wagerResponse.
 * @param root0 second argument properties
 * @param root0.drawInfo An object where's the properties consists of `${productId}_${drawNumber}` containing information
 * about the draws close time, jackpot/funds information etc.
 * @param root0.trackingContext An optional context object
 * @param root0.typeOfShares A string with type of share if marketplace wager
 * @returns The transaction object to be sent to the data layers
 */
const createTransaction = <TPayable extends IPayable>(
  payableData: PaidPayableWagerItem<TPayable>,
  {
    drawInfo,
    trackingContext,
    typeOfShares,
  }: {
    drawInfo?: Awaited<ReturnType<typeof fetchAdditionalDrawInfo>>;
    trackingContext?: TrackingContext;
    typeOfShares?: MarketplaceShare;
  }
): Transaction | undefined => {
  // const compHandler = competitionHandler();
  const productBet = payableData.payable.bets.find((bet) => !Object.hasOwn(addonProducts, bet.product));
  const totalPrice = payableData.dto.cost;
  const { bagType } = payableData.payable;
  const { bag, uniqueTrackingId } = trackingContext ?? {};

  const relatedAddonProductId = payableData.payable.addonProduct;

  const items = payableData.payable.bets.map((bet) => {
    const addonProduct = getObjectValueByKey(addonProducts, bet.product);
    if (addonProduct) {
      const [addonProductName, itsAddonProductId] = addonProduct;
      return {
        SKU: itsAddonProductId,
        productName: addonProductName,
        price: bet.price,
        quantity: 1,
      } satisfies TransactionItem;
    }

    const extraTracking: Record<string, unknown> = {};
    const extraInfoKey = `${bet.product}_${bet.drawNumber}` satisfies `${Product}_${number}`;

    const extra = drawInfo?.draws.get(extraInfoKey);
    if (extra) {
      const fund = {
        extrapengar: extra.fund.extraMoney,
        rullpott: extra.fund.rolloverIn,
      };

      Object.assign(
        extraTracking,
        {
          weekRound: extra.regOpenTime ? getISOWeek(parseISO(extra.regOpenTime)) : 0,
        },
        fund
      );
    }

    const jp = drawInfo?.jackpots.get(bet.product);
    if (jp) {
      const jackpot = jp;
      Object.assign(extraTracking, {
        jackpot: jackpot.length > 0 ? jackpot.join('|') : '0.00',
      });
    }

    const quickpick = bet.quickPickValue;
    if (quickpick) {
      Object.assign(extraTracking, {
        quickpick,
      });
    }

    const compType = 'None';

    if (payableData.payable.isCompetition()) {
      logger.warn('Competition tracking is not implemented yet');
      // const competition = compHandler.getActiveCompetition(bet.productId);
      // compType = competition?.getCompType() || compType;
    }

    const productName = relatedAddonProductId
      ? (getObjectValueByKey(relatedAddonProductName, relatedAddonProductId) ?? bet.productName)
      : bet.productName;

    return {
      SKU: bet.itsProductId,
      productName,
      price: bet.price,
      quantity: 1,
      optCategory: {
        ...extraTracking,
        rounds: bet.numberOfDraws,
        system: bet.getSystemLabel(),
        competition: compType,
        draw: bet.drawNumber,
        bag,
        bagType,
        uniqueTrackingId,
        shares: Boolean(typeOfShares),
        typeOfShares,
      },
    } satisfies TransactionItem;
  });

  if (items.length === 0) {
    return undefined;
  }

  const product = relatedAddonProductId
    ? getObjectValueByKey(relatedAddonProductName, relatedAddonProductId)
    : productBet?.productName;

  return {
    orderId: payableData.dto.transactionId,
    totalAmount: totalPrice,
    items: items as [TransactionItem, ...TransactionItem[]],
    optTaxAmount: 0,
    optShippingAmount: 0,
    optProductName: product,
  };
};

/**
 * Extracts every unique product id and draw number combinations from the paid wagers if there's more than one wager
 * paid for the same draw (eg. external systems).
 *
 * @param payableItemsData An array of objects with wagerId, wager and wagerResponse.
 * @returns An array of unique objects containing product id and draw number
 */
const extractProductInfoFromWagers = <TPayable extends IPayable>(
  payableItemsData: readonly PaidPayableItem<TPayable>[]
): ProductToTrack[] => {
  const productInfo = new Map<`${Product}_${number}`, ProductToTrack>();

  for (const { payable: wager } of payableItemsData) {
    if (!isWager(wager)) {
      continue;
    }

    for (const { product, drawNumber } of wager.bets) {
      if (!ADDITIONAL_PRODUCTS.includes(product)) {
        continue;
      }

      productInfo.set(`${product}_${drawNumber}`, { product, drawNumber });
    }
  }

  return [...productInfo.values()];
};

/**
 * Extracts if a wager is marketplace wager and then gets what type of marketplace wager.
 *
 * @param payableItemsData An array of objects with wagerId, wager and wagerResponse.
 * @returns string of value for what type of marketplace shares
 */
const getMarketplaceInfo = <TPayable extends IPayable>(
  payableItemsData: readonly PaidPayableItem<TPayable>[]
): MarketplaceShare | undefined => {
  let typeOfShare: MarketplaceShare | undefined;

  for (const { payable: wager } of payableItemsData) {
    if (!isWager(wager)) {
      continue;
    }

    typeOfShare = convertRoleToTypeOfShare(wager.customer);

    if (typeOfShare) {
      break;
    }
  }

  return typeOfShare;
};

/**
 * Function to track provided wager data.
 * Creates and sends the transaction information to the data layers (GTM, EDDL, Luna etc).
 *
 * @param payableItemsData - An array of objects with wagerId, wager and wagerResponse.
 * @param trackingContext An optional context object
 * @param trackTransaction An optional function to track ecommerce analytics events, used for tracking payment events
 */
export async function trackWagers<TPayable extends IPayable>(
  payableItemsData: readonly PaidPayableItem<TPayable>[],
  trackingContext?: TrackingContext,
  trackTransaction?: typeof trackEcommerce
): Promise<void> {
  const uniqueProducts = extractProductInfoFromWagers<TPayable>(payableItemsData);
  const marketplaceInfo = getMarketplaceInfo<TPayable>(payableItemsData);
  const drawInfo = await fetchDrawInfo(uniqueProducts);

  for (const payableData of payableItemsData) {
    const { payable } = payableData;
    if (!isWager(payable)) {
      continue;
    }

    const trackingWager = createTransaction(
      { ...payableData, payable },
      { drawInfo, trackingContext, typeOfShares: marketplaceInfo }
    );
    if (!trackingWager) {
      logger.warn('Skipping ecommerce tracking due to missing wager items', {
        wagerId: payableData.payable.localId,
      });
      continue;
    }

    if (trackTransaction) {
      trackTransaction(trackingWager);
    }

    if (!trackTransaction) {
      logger.info('No trackTransaction function provided, unable to track transaction event for wager', {
        wagerId: payableData.payable.localId,
      });
    }
  }
}
